/**
 * Achat des SKILLCARDS DANS l'app native — Android (Google Play Billing) et
 * iOS (App Store) via RevenueCat, migration 0351.
 *
 * Aucun produit store par carte : le serveur (`create-skillcard-iap-order`)
 * calcule le prix exact, choisit le PALIER le plus proche (`jr_sk_1999` =
 * 19,99 €…) et crée la commande. L'app achète ce palier ; le webhook
 * RevenueCat rattache la transaction à la commande et donne les droits.
 *
 * Mêmes 26 paliers consommables `jr_sk_NNNN` sur les deux stores (App Store
 * 3.1.1 : contenu numérique ⇒ achat intégré obligatoire sur iOS). Cadeau,
 * achat pour un enfant, « faire payer par un proche » : web seulement.
 */
import { isIOSNative, isNativeApp } from '@/lib/platform';
import { supabase } from '@/lib/supabase';
import { isRevenueCatReady } from '@/lib/checkout/native';
import { skillcardIapTierFor, type SkillcardOfferKind } from '@jeraisonne/db/cards';

/** Achat de SKILLCARDS possible ici : web (Stripe), Android (Google Play), iOS (App Store). */
export function canBuySkillcardsInApp(): boolean {
  return true;
}

/** Vrai quand l'achat passe par un store (prix = palier du store). */
export function usesStoreBilling(): boolean {
  return isNativeApp();
}

export type SkillcardStore = 'play_store' | 'app_store';

/** Store de l'appareil (null sur le web). */
export function currentSkillcardStore(): SkillcardStore | null {
  if (!isNativeApp()) return null;
  return isIOSNative() ? 'app_store' : 'play_store';
}

/** Nom affiché du store ('Google Play' / 'l’App Store'), null sur le web. */
export function storeLabel(store: SkillcardStore | null = currentSkillcardStore()): string | null {
  if (store === 'app_store') return 'l’App Store';
  if (store === 'play_store') return 'Google Play';
  return null;
}

/**
 * Prix affiché d'une offre quand l'achat passe par un store : celui du
 * palier que le serveur choisira (même règle que `skillcard_iap_tier_for`).
 * Le palier remplace le prix exact ; l'économie affichée reste celle du lot.
 */
export function storeQuote<T extends { price_cents: number }>(
  q: T,
  storeBilling = usesStoreBilling(),
): T {
  if (!storeBilling || q.price_cents <= 0) return q;
  const tier = skillcardIapTierFor(q.price_cents);
  return tier == null ? q : { ...q, price_cents: tier };
}

/** Mention sous les offres quand l'achat passe par un store (null sur le web). */
export function storePaymentNote(
  store: SkillcardStore | null = currentSkillcardStore(),
): string | null {
  const label = storeLabel(store);
  if (!label) return null;
  return `Paiement unique via ${label}, sans abonnement. Prix arrondi au palier de prix du store le plus proche.`;
}

export interface SkillcardIapOrder {
  purchase_id: string;
  product_id: string;
  price_cents: number;
  return_path: string;
}

/** Erreur « l'élève a fermé la feuille du store » : à ne pas afficher comme un échec. */
export const IAP_CANCELLED = 'user_cancelled';

export function isUserCancelled(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: unknown; message?: unknown; userCancelled?: unknown };
  return (
    e.userCancelled === true ||
    e.code === '1' ||
    e.code === 1 ||
    e.code === 'PURCHASE_CANCELLED_ERROR' ||
    /cancel/i.test(String(e.message ?? ''))
  );
}

/**
 * Achète un lot sur le store de l'appareil et renvoie le chemin INTERNE de retour
 * (`/card/…/acheter?purchase=…`), qui attend la confirmation du webhook.
 */
export async function purchaseSkillcardInStore(params: {
  kind: SkillcardOfferKind;
  targetId: string;
  cardId: string;
  returnPath?: string | null;
  fromPackId?: string | null;
}): Promise<string> {
  const store = currentSkillcardStore();
  if (!store || !isRevenueCatReady()) throw new Error('store_unavailable');

  const { data, error } = await supabase.functions.invoke('create-skillcard-iap-order', {
    body: {
      kind: params.kind,
      target_id: params.targetId,
      card_id: params.cardId,
      return_path: params.returnPath ?? null,
      from_pack_id: params.fromPackId ?? null,
      store,
    },
  });
  const order = data as (SkillcardIapOrder & { error?: string }) | null;
  if (error || !order?.product_id) {
    throw new Error(order?.error ?? (await invokeErrorCode(error)) ?? 'unknown');
  }

  const { Purchases, PRODUCT_CATEGORY } = await import('@revenuecat/purchases-capacitor');
  const { products } = await Purchases.getProducts({
    productIdentifiers: [order.product_id],
    type: PRODUCT_CATEGORY.NON_SUBSCRIPTION,
  });
  const product = products.find((p) => p.identifier === order.product_id);
  if (!product) throw new Error('store_product_missing');

  try {
    await Purchases.purchaseStoreProduct({ product });
  } catch (err) {
    if (isUserCancelled(err)) throw new Error(IAP_CANCELLED);
    console.error('[skillcard-iap] purchase failed', err);
    throw new Error('store_failed');
  }
  return order.return_path;
}

async function invokeErrorCode(error: unknown): Promise<string | null> {
  const ctx = (error as { context?: Response } | null)?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      return ((await ctx.json()) as { error?: string }).error ?? null;
    } catch {
      return null;
    }
  }
  return null;
}
